/* eslint-disable no-underscore-dangle */
import { Button, Form, FormCard, PageHeader } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Checkbox, InputNumber, Switch } from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { PageWrapper } from '../components'
import { handleOnFinishFailed } from '../formComponents/utils'
import {
  TravelRateCardLevel,
  useCreateTravelRateCard,
  useGetTravelRateCardSettings,
  useUpdateTravelRateCardSettings
} from '../hooks/useTravelRateCardSettings'
import SettingsPaywall, {
  useRosteringPaywall
} from './components/SettingsPaywall'

enum TravelRateCardStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled'
}

type TravelTimeAndMileageForm = {
  status: TravelRateCardStatus
  travelTimePayRate?: number
  useTravelTimePayRate: boolean
  mileageReimbursementRate?: number
  useMileageReimbursementRate: boolean
  maximumPaidBreak?: number
  useMaximumPaidBreak: boolean
}

const TravelTimeAndMileageSettings = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [featureEnabled, setFeatureEnabled] = useState(false)
  const { isLocked } = useRosteringPaywall()

  const useTravelTimePayRate = useWatch('useTravelTimePayRate', form)
  const useMileageReimbursementRate = useWatch(
    'useMileageReimbursementRate',
    form
  )
  const useMaximumPaidBreak = useWatch('useMaximumPaidBreak', form)

  const { data: rateCards } = useGetTravelRateCardSettings()
  const { createSettings } = useCreateTravelRateCard()
  const { updateSettings } = useUpdateTravelRateCardSettings()

  const handleFormValuesChange = (
    changedValues: Partial<TravelTimeAndMileageForm>
  ) => {
    const currentValues = form.getFieldsValue()
    const rateCard = rateCards?.[0]

    if (!rateCard) {
      setIsDirty(true)
      return
    }

    const isChanged =
      featureEnabled !== (rateCard.status === TravelRateCardStatus.ENABLED) ||
      (currentValues.useTravelTimePayRate
        ? currentValues.travelTimePayRate
        : 0) !== rateCard.timePayRate ||
      (currentValues.useMileageReimbursementRate
        ? currentValues.mileageReimbursementRate
        : 0) !== rateCard.mileagePayRate ||
      (currentValues.useMaximumPaidBreak
        ? currentValues.maximumPaidBreak
        : 0) !== rateCard.maximumPaidBreak

    setIsDirty(isChanged)

    if (
      'useMileageReimbursementRate' in changedValues &&
      changedValues.useMileageReimbursementRate
    ) {
      const currentRate = form.getFieldValue('mileageReimbursementRate')
      if (!currentRate) {
        form.setFieldsValue({ mileageReimbursementRate: 0.45 })
      }
    }
  }

  useEffect(() => {
    if (rateCards && rateCards.length > 0) {
      const siteRateCard = rateCards.find(
        (card) => card.levelType === TravelRateCardLevel.SITE
      )
      if (siteRateCard) {
        form.setFieldsValue({
          status: siteRateCard.status,
          travelTimePayRate: siteRateCard.timePayRate,
          mileageReimbursementRate: siteRateCard.mileagePayRate,
          maximumPaidBreak: siteRateCard.maximumPaidBreak,
          useTravelTimePayRate: !!siteRateCard.timePayRate,
          useMileageReimbursementRate: !!siteRateCard.mileagePayRate,
          useMaximumPaidBreak: !!siteRateCard.maximumPaidBreak
        })
        setFeatureEnabled(siteRateCard.status === TravelRateCardStatus.ENABLED)
      }
    }
  }, [rateCards, form])

  const handleOnFinish = async (values: TravelTimeAndMileageForm) => {
    try {
      setIsUpdateLoading(true)

      const rateCard = rateCards?.[0]

      const payload = {
        _id: rateCard?._id,
        levelType: TravelRateCardLevel.SITE,
        status: featureEnabled
          ? TravelRateCardStatus.ENABLED
          : TravelRateCardStatus.DISABLED,
        timePayRate:
          featureEnabled && values.useTravelTimePayRate
            ? values.travelTimePayRate
            : undefined,
        mileagePayRate:
          featureEnabled && values.useMileageReimbursementRate
            ? values.mileageReimbursementRate
            : undefined,
        maximumPaidBreak:
          featureEnabled && values.useMaximumPaidBreak
            ? values.maximumPaidBreak
            : undefined
      }

      if (rateCard?._id) {
        await updateSettings(payload)
      } else {
        await createSettings(payload)
      }

      showToast({ title: t('SETTINGS_UPDATED') })
      setIsDirty(false)
    } catch (_) {
      showToast({
        title: t('SETTINGS_ERROR'),
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const formatCurrency = (value: string | number | undefined) => {
    if (value === undefined || value === '') {
      return ''
    }

    return typeof value === 'string' ? value : value.toFixed(2)
  }

  const handleFeatureToggle = (checked: boolean) => {
    setFeatureEnabled(checked)
    form.setFieldsValue({
      status: checked
        ? TravelRateCardStatus.ENABLED
        : TravelRateCardStatus.DISABLED
    })
    setIsDirty(true)
  }

  return (
    <>
      <StyledPageWrapper>
        <PageHeader
          title={t('TRAVEL_TIME_AND_MILEAGE')}
          subtitle={t('TRAVEL_TIME_AND_MILEAGE_INFO')}
          sticky
        >
          {!isLocked && (
            <Button
              isLoading={isUpdateLoading}
              variant="primary"
              type="submit"
              disabled={!isDirty}
              onClick={form.submit}
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('SAVE_CHANGES')}
            </Button>
          )}
        </PageHeader>
        <SettingsPaywall startingText={t('TRAVEL_TIME_AND_MILEAGE_IS')}>
          <Form<TravelTimeAndMileageForm>
            form={form}
            onValuesChange={handleFormValuesChange}
            onFinish={handleOnFinish}
            onFinishFailed={({ errorFields }) => {
              handleOnFinishFailed(errorFields)
            }}
            initialValues={{
              status: TravelRateCardStatus.DISABLED,
              useTravelTimePayRate: false,
              useMileageReimbursementRate: false,
              useMaximumPaidBreak: false
            }}
          >
            {featureEnabled ? (
              <FormCard
                title={
                  <AntdForm.Item
                    name="status"
                    colon={false}
                    label={<Text>{t('ENABLE_TRAVEL_TIME_AND_MILEAGE')}</Text>}
                  >
                    <Switch
                      checked={featureEnabled}
                      onChange={handleFeatureToggle}
                    />
                  </AntdForm.Item>
                }
              >
                <CheckboxContainer>
                  <AntdForm.Item
                    name="useTravelTimePayRate"
                    valuePropName="checked"
                  >
                    <Checkbox>
                      <Text>{t('USE_TRAVEL_TIME_PAY_RATE')}</Text>
                    </Checkbox>
                  </AntdForm.Item>

                  {useTravelTimePayRate && (
                    <InputContainer>
                      <AntdForm.Item
                        name="travelTimePayRate"
                        rules={[
                          {
                            required: true,
                            message: t('FIELD_CANNOT_BE_EMPTY')
                          },
                          {
                            type: 'number',
                            min: 0.01,
                            max: 9999.99,
                            message: t('VALUE_MUST_BE_BETWEEN', {
                              min: '0.01',
                              max: '9999.99'
                            })
                          }
                        ]}
                      >
                        <InputNumber
                          min={0.01}
                          max={9999.99}
                          precision={2}
                          formatter={formatCurrency}
                          placeholder={t('AMOUNT_PLACEHOLDER')}
                          addonAfter={<Text>{t('GBP/hour')}</Text>}
                          parser={(value) => {
                            if (value === undefined || value === '') {
                              return ''
                            }

                            return Number(value)
                          }}
                        />
                      </AntdForm.Item>
                    </InputContainer>
                  )}
                </CheckboxContainer>

                <CheckboxContainer>
                  <AntdForm.Item
                    name="useMileageReimbursementRate"
                    valuePropName="checked"
                  >
                    <Checkbox>
                      <Text>{t('USE_MILEAGE_REIMBURSEMENT_RATE')}</Text>
                    </Checkbox>
                  </AntdForm.Item>

                  {useMileageReimbursementRate && (
                    <InputContainer>
                      <AntdForm.Item
                        name="mileageReimbursementRate"
                        rules={[
                          {
                            required: true,
                            message: t('FIELD_CANNOT_BE_EMPTY')
                          },
                          {
                            type: 'number',
                            min: 0.01,
                            max: 9999.99,
                            message: t('VALUE_MUST_BE_BETWEEN', {
                              min: '0.01',
                              max: '9999.99'
                            })
                          }
                        ]}
                      >
                        <InputNumber
                          min={0.01}
                          max={9999.99}
                          precision={2}
                          addonAfter={<Text>{t('GBP/mile')}</Text>}
                          formatter={formatCurrency}
                          parser={(value) => {
                            if (value === undefined || value === '') {
                              return ''
                            }

                            return Number(value)
                          }}
                        />
                      </AntdForm.Item>
                    </InputContainer>
                  )}
                </CheckboxContainer>

                <CheckboxContainer>
                  <AntdForm.Item
                    name="useMaximumPaidBreak"
                    valuePropName="checked"
                  >
                    <Checkbox>
                      <Text>{t('SET_MAXIMUM_TIME_BETWEEN_VISITS')}</Text>
                    </Checkbox>
                  </AntdForm.Item>

                  {useMaximumPaidBreak && (
                    <InputContainer>
                      <AntdForm.Item
                        name="maximumPaidBreak"
                        rules={[
                          {
                            required: true,
                            message: t('FIELD_CANNOT_BE_EMPTY')
                          },
                          {
                            type: 'number',
                            min: 1,
                            max: 9999,
                            message: t('VALUE_MUST_BE_BETWEEN', {
                              min: '1',
                              max: '9999'
                            })
                          }
                        ]}
                      >
                        <InputNumber
                          min={1}
                          max={9999}
                          precision={0}
                          addonAfter={<Text>{t('MINUTES')}</Text>}
                          placeholder={t('MINUTES_PLACEHOLDER')}
                        />
                      </AntdForm.Item>
                    </InputContainer>
                  )}
                </CheckboxContainer>
              </FormCard>
            ) : (
              <FormCard
                title={
                  <AntdForm.Item
                    name="status"
                    colon={false}
                    label={<Text>{t('ENABLE_TRAVEL_TIME_AND_MILEAGE')}</Text>}
                  >
                    <Switch
                      checked={featureEnabled}
                      onChange={handleFeatureToggle}
                    />
                  </AntdForm.Item>
                }
              />
            )}
          </Form>
        </SettingsPaywall>
      </StyledPageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('TRAVEL_TIME_AND_MILEAGE_NAVIGATION_MODAL_CONTENT')}
      />
    </>
  )
}

export default TravelTimeAndMileageSettings

const StyledPageWrapper = styled(PageWrapper)`
  padding-left: 24px;
  padding-right: 24px;

  & > *:first-child {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`

const CheckboxContainer = styled.div``

const InputContainer = styled.div``
